import AddIcon from '@mui/icons-material/Add';
import Fab from '@mui/material/Fab';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
	ExposureConfigurationResponse,
	PerilConfigurationResponse,
	PolicyConfiguration,
} from '../../../../api/ConfigApi';
import { AuthData, HIDE_PERIL_LIMITS_ENABLE } from '../../../../context/auth';
import { getNewPeril } from '../../../../context/policy';
import { RenewalData } from '../../../../context/RenewalProvider';
import { checkExposureList, sortByObjectKey } from '../../../../util/util';
import { StyledModalContentContainer } from '../../../common';
import DefaultDialog from '../../../DefaultDialog';
import { errorToast } from '../../../Toast';
import ReactSelect, { SelectValue } from '../../fields/ReactSelect';

interface Props {
	authData: AuthData;
	renewalData: RenewalData;
	policyConfig: PolicyConfiguration;
}

export default function AddExposureModal(props: Props) {
	const { t } = useTranslation();
	const { getValues, formState, reset } = useFormContext();
	const { dirtyFields } = formState;
	const { policyConfig, renewalData, authData } = props;
	const exposureConfigs = policyConfig.exposures;
	const sortedExposures = sortByObjectKey(exposureConfigs, 'displayName');
	const exposureOptions = sortedExposures.map(
		(eC: ExposureConfigurationResponse) => ({
			value: eC.name,
			label: eC.displayName,
		}),
	);

	const [isOpen, setIsOpen] = useState(false);
	// choose first one by default
	const initialValue = sortedExposures[0]
		? { value: sortedExposures[0].name, label: sortedExposures[0].displayName }
		: { value: '', label: '' };
	const [selectedExposure, setSelectedExposure] = useState(initialValue);
	const close = () => setIsOpen(false);
	const open = () => setIsOpen(true);

	const { setLoading, saveFastForm } = renewalData;
	const navigate = useNavigate();
	const { policyId, renewalId } = useParams();

	const hidePerilLimitsEnabled =
		authData?.auth?.configProperties?.[HIDE_PERIL_LIMITS_ENABLE] ?? false;

	const addExposure = async () => {
		const exposureConfig = policyConfig.exposures.find(
			(exposure) => exposure.name === selectedExposure.value,
		);
		const newPerils = exposureConfig.perils.map(
			(peril: PerilConfigurationResponse) =>
				getNewPeril(peril, hidePerilLimitsEnabled),
		);

		const addExposureReq = {
			exposureName: selectedExposure.value,
			perils: newPerils,
		};

		try {
			setLoading(true);
			await close();
			// call saveFastForm so we can retrieve the addedExposure object with an assigned locator
			const updatedRenewal = await saveFastForm(
				getValues(),
				dirtyFields,
				reset,
				{
					addExposure: addExposureReq,
				},
			);
			const indexOfLastAddedExposure = updatedRenewal.addExposures.length - 1;
			navigate(
				`/policy/${policyId}/renewals/${renewalId}/exposures/added-${indexOfLastAddedExposure}`,
			);
		} catch (error) {
			errorToast('Failed to add exposure');
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	return (
		<>
			<Fab
				color='primary'
				onClick={() => checkExposureList(sortedExposures, addExposure, open)}
				sx={{ position: 'fixed', bottom: 20, right: 20 }}
				variant='extended'
			>
				<AddIcon sx={{ mr: 1 }} /> {`Add ${t('exposure.singular.upper')}`}
			</Fab>
			<DefaultDialog
				actions={[
					{ text: 'Add', onClick: addExposure },
					{ text: 'Cancel', onClick: close },
				]}
				open={isOpen}
				onClose={close}
				title={`Add ${t('exposure.singular.upper')}`}
			>
				<StyledModalContentContainer>
					<div style={{ width: '50%', height: '40px' }}>
						<ReactSelect
							id='add-exposure-selector'
							options={exposureOptions}
							value={selectedExposure}
							onChange={(opt: SelectValue) => setSelectedExposure(opt)}
							disableOpenMenuOnFocus
						/>
					</div>
				</StyledModalContentContainer>
			</DefaultDialog>
		</>
	);
}
